var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
var _a;
import { BehaviorSubject, merge, Subject } from 'rxjs';
import { distinctUntilChanged, map, scan, skip, startWith, tap } from 'rxjs/operators';
import { VALIDATE_CODES } from './rules/codes';
import deepEqual from 'fast-deep-equal';
var VALIDATION_ERRORS = (_a = {},
    _a[VALIDATE_CODES.default] = 'The value in this field is invalid',
    _a[VALIDATE_CODES.required] = 'The field is required',
    _a[VALIDATE_CODES.maxLength] = 'The maximum length is {{length}}',
    _a[VALIDATE_CODES.minLength] = 'The minimum length is {{length}}',
    _a[VALIDATE_CODES.maxNumber] = 'The maximum number is {{max}}',
    _a[VALIDATE_CODES.minNumber] = 'The minimum number is {{min}}',
    _a);
var FieldState = /** @class */ (function () {
    function FieldState() {
        this.state = {};
        this.subject = new BehaviorSubject({});
    }
    FieldState.prototype.asObservable = function () {
        return this.subject.asObservable();
    };
    FieldState.prototype.getValue = function () {
        return this.state;
    };
    FieldState.prototype.update = function (key, value) {
        this.state[key] = value;
        this.subject.next(this.state);
    };
    return FieldState;
}());
var Form = /** @class */ (function () {
    function Form(model, config) {
        if (config === void 0) { config = {}; }
        this.model = model;
        this.config = config;
        this.errorState = new FieldState();
        this.displayErrorState = new FieldState();
        this.additionalValidationRules = {};
        this.internalInitialValue = null;
        this.compare = deepEqual;
        this.makeInitSubject = new Subject();
        if (config.comparator) {
            this.compare = config.comparator;
        }
        try {
            this.internalInitialValue = this.model.getValue();
            this.validate();
        }
        catch (_a) { }
    }
    Form.prototype.makeValueInitial = function () {
        this.internalInitialValue = this.model.getValue();
        this.makeInitSubject.next(true);
    };
    Object.defineProperty(Form.prototype, "fields", {
        get: function () {
            var _this = this;
            if (this.internalFields) {
                return this.internalFields;
            }
            var newObj = {};
            var modelValue = this.model.getValue();
            var _loop_1 = function (key) {
                if (!modelValue.hasOwnProperty(key)) {
                    return "continue";
                }
                newObj[key] = {
                    getName: function () { return key; },
                    getValue: function () {
                        return _this.model.getValue()[key];
                    },
                    asObservable: function () {
                        return _this.model.fieldChanges(key).pipe(map(function (v) { return v.value; }));
                    },
                    update: function (newValue) {
                        if (_this.model.update && newValue !== _this.model.getValue()[key]) {
                            _this.hideError(key);
                            _this.model.update(function (value) {
                                value[key] = newValue;
                            });
                        }
                    },
                    touch: function () {
                        _this.validateFiled(key);
                        _this.displayError(key);
                    },
                    getMeta: function () {
                        return {
                            displayError: !!_this.displayErrorState.getValue()[key]
                        };
                    },
                    getMetaObservable: function () {
                        return _this.displayErrorState.asObservable().pipe(skip(1), map(function () { return !!_this.displayErrorState.getValue()[key]; }), distinctUntilChanged(), map(function (value) { return ({ displayError: value }); }));
                    },
                    getError: function () {
                        return String(_this.errorState.getValue()[key] || '');
                    },
                    getErrorObservable: function () {
                        return _this.errorState.asObservable().pipe(skip(1), map(function () {
                            return String(_this.errorState.getValue()[key] || '');
                        }), distinctUntilChanged());
                    },
                    setError: function (error) {
                        _this.setError(key, error);
                    },
                    addValidationRule: function (rule) {
                        var _a;
                        if (!_this.additionalValidationRules[key]) {
                            _this.additionalValidationRules[key] = [];
                        }
                        (_a = _this.additionalValidationRules[key]) === null || _a === void 0 ? void 0 : _a.push(rule);
                        _this.validateFiled(key);
                        return function () {
                            var _a, _b;
                            if (Array.isArray(_this.additionalValidationRules[key])) {
                                _this.additionalValidationRules[key] = (_b = (_a = _this.additionalValidationRules) === null || _a === void 0 ? void 0 : _a[key]) === null || _b === void 0 ? void 0 : _b.filter(function (i) { return i !== rule; });
                                _this.validateFiled(key);
                            }
                        };
                    }
                };
            };
            for (var key in modelValue) {
                _loop_1(key);
            }
            this.internalFields = newObj;
            return this.internalFields;
        },
        enumerable: true,
        configurable: true
    });
    Form.prototype.displayError = function (key, value) {
        if (typeof value === 'string') {
            this.setError(key, value);
        }
        this.displayErrorState.update(key, true);
    };
    Form.prototype.hideError = function (key) {
        this.displayErrorState.update(key, false);
    };
    Form.prototype.getValue = function () {
        return this.model.getValue();
    };
    Form.prototype.validate = function () {
        var modelValue = this.getValue();
        for (var key in modelValue) {
            if (!modelValue.hasOwnProperty(key)) {
                continue;
            }
            this.validateFiled(key);
        }
        return !this.hasErrors();
    };
    Form.prototype.validateAndDisplayErrors = function () {
        var result = this.validate();
        this.displayErrors();
        return result;
    };
    Form.prototype.displayErrors = function () {
        var modelValue = this.getValue();
        for (var key in modelValue) {
            if (!modelValue.hasOwnProperty(key)) {
                continue;
            }
            this.displayError(key);
        }
    };
    Form.prototype.setError = function (key, value) {
        if (this.errorState.getValue()[key] === value) {
            return;
        }
        this.errorState.update(key, value);
    };
    Form.prototype.hasChanges = function () {
        return !this.compare(this.internalInitialValue, this.getValue());
    };
    Form.prototype.getMetaValue = function () {
        return {
            isValid: this.validate(),
            hasChanges: this.hasChanges()
        };
    };
    Form.prototype.metaChanges = function () {
        var _this = this;
        var getMetaValue = function () {
            return {
                isValid: !_this.hasErrors(),
                hasChanges: _this.hasChanges()
            };
        };
        return merge(this.model.asObservable().pipe(tap(function () { return _this.validate(); })), this.errorState.asObservable(), this.makeInitSubject.asObservable()).pipe(startWith(getMetaValue()), map(function () { return getMetaValue(); }), distinctUntilChanged(deepEqual), scan(function (acc, curr) {
            return {
                value: curr,
                prevValue: acc.value
            };
        }, {}), skip(1));
    };
    Form.prototype.resetToInitialValue = function () {
        if (this.internalInitialValue !== null) {
            this.model.setValue(this.internalInitialValue);
        }
    };
    Form.prototype.validateFiled = function (key) {
        var schema = this.config.validationSchema;
        var schemaRules = schema && Array.isArray(schema[key]) ? schema[key] : [];
        var additionalRules = this.additionalValidationRules[key] || [];
        var rules = Array.isArray(schemaRules) ? __spreadArrays(schemaRules) : [];
        if (Array.isArray(additionalRules)) {
            rules = rules.concat(additionalRules);
        }
        var err = '';
        var modelValue = this.model.getValue();
        rules.some(function (rule) {
            err = rule(modelValue[key], modelValue, VALIDATION_ERRORS) || '';
            return err;
        });
        this.setError(key, err);
    };
    Form.prototype.hasErrors = function () {
        var modelValue = this.model.getValue();
        var errors = this.errorState.getValue();
        for (var key in errors) {
            if (!modelValue.hasOwnProperty(key)) {
                continue;
            }
            if (errors[key]) {
                return true;
            }
        }
        return false;
    };
    return Form;
}());
export { Form };
//# sourceMappingURL=Form.js.map